import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client/public/index";
import { BASE_GRAPHQL_URL } from "config/apiUrls";
import { OAUTH2_ENABLED } from "config/auth";
import { useAuthToken, useLogoutWithoutApollo } from "hooks/auth/auth";
import { useEffect, useMemo, useRef } from "react";
import { fixStockLogicForFE } from "utils/helperFuncs";
import { clientMetadataLink } from "./clientMetadataLink";
import { createErrorLink } from "./errorLink";
import uploadProgressFetch from "./uploadProgressFetch";

const DEFAULT_OPTIONS = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

const httpLink = createUploadLink({
  uri: BASE_GRAPHQL_URL,
  // Chỉ operation truyền fetchOptions.onUploadProgress mới dùng XHR (tiến độ/hủy); còn lại vẫn là fetch.
  fetch: uploadProgressFetch,
});

/**
 * loyaltyPackage là root chung cho list/detail/mutation BE.
 * - `merge: true` trên Query.loyaltyPackage: mutation chỉ trả 1 field (vd. switchPackageStatus)
 *   không được ghi đè mất listPackages / listPeriods đã cache.
 * - `keyArgs` trên LoyaltyPackageQuery / PromotionCampaignQuery: mỗi list cache theo variables riêng —
 *   chuyển tab campaign không ghi đè cache tab khác.
 */
export const cache = new InMemoryCache({
  typePolicies: {
    Product: {
      fields: {
        /**
         * BE trả `fullAttributeValues` không theo thứ tự ổn định — cùng một bộ dữ liệu
         * nhưng vị trí trong mảng bị xáo trộn khác nhau ở mỗi lần gọi cùng 1 query/variables.
         * Apollo so sánh deep-equal giữa 2 lần đọc cache để quyết định "có gì mới không";
         * mảng đổi thứ tự khiến so sánh đó luôn ra "khác nhau" → dưới fetchPolicy
         * "cache-and-network" (xem useStaleFetchPolicy trong hooks/campaign/index.js),
         * Apollo tự động refetch mỗi khi thấy "khác" → BE lại xáo trộn khác → lặp vô hạn
         * (từng gây loop request GetDetailPromotionCampaign ở lần vào thứ 2).
         * Sort ổn định theo attributeID trước khi ghi cache để loại bỏ tính bất định này.
         * An toàn vì mọi nơi trong UI đều tra theo `.find(item => item.attributeID === ...)`,
         * không phụ thuộc thứ tự mảng.
         */
        fullAttributeValues: {
          merge(_existing, incoming) {
            if (!Array.isArray(incoming)) return incoming;
            return [...incoming].sort((a, b) => (a?.attributeID > b?.attributeID ? 1 : -1));
          },
        },
      },
    },
    Query: {
      fields: {
        loyaltyPackage: {
          merge: true,
        },
        promotionCampaign: {
          merge: true,
        },
      },
    },
    LoyaltyPackageQuery: {
      fields: {
        listPackages: {
          keyArgs: ["filters", "pagination"],
        },
        listPeriods: {
          keyArgs: ["filters", "pagination"],
        },
        getPackage: {
          keyArgs: ["id"],
        },
        getPeriod: {
          keyArgs: ["id"],
        },
      },
    },
    LoyaltyPackageOps: {
      fields: {
        adminListLoyaltyPackages: {
          keyArgs: ["filters", "pagination"],
        },
        adminListLoyaltyPackagePeriods: {
          keyArgs: ["filters", "pagination"],
        },
        adminGetLoyaltyPackage: {
          keyArgs: ["id"],
        },
        adminGetLoyaltyPackagePeriod: {
          keyArgs: ["id"],
        },
      },
    },
    PromotionCampaignQuery: {
      fields: {
        get: {
          keyArgs: ["id"],
        },
        pagination: {
          keyArgs: ["filters", "pagination", "sort"],
        },
        count: {
          keyArgs: ["filters"],
        },
      },
    },
    PromotionCampaignOps: {
      fields: {
        pagination: {
          keyArgs: ["filters", "pagination", "sort"],
        },
        count: {
          keyArgs: ["filters"],
        },
      },
    },
  },
});

export const useAppApolloClient = () => {
  const { token } = useAuthToken();
  const { logoutWithoutApollo } = useLogoutWithoutApollo();

  // Dùng ref để lưu token mới nhất
  const tokenRef = useRef(token);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // logoutWithoutApollo không được memo hóa ở nguồn (đổi identity mỗi render) nên không thể
  // đưa thẳng vào deps của useMemo bên dưới — nếu không apolloClient (và mọi query dùng nó,
  // vd. ConsultRequest) sẽ bị tạo lại và refetch ở mỗi render, gây lặp gọi API vô hạn.
  const logoutRef = useRef(logoutWithoutApollo);
  useEffect(() => {
    logoutRef.current = logoutWithoutApollo;
  }, [logoutWithoutApollo]);

  const apolloClient = useMemo(() => {
    const dynamicAuthLink = setContext(async (_, { headers }) => {
      let currentToken = tokenRef.current;

      if (OAUTH2_ENABLED) {
        currentToken = null;
      }

      return {
        headers: {
          ...headers,
          authorization: currentToken ? `Bearer ${currentToken}` : "",
        },
      };
    });

    const errorLink = createErrorLink(() => logoutRef.current());

    const fixStockLink = new ApolloLink((operation, forward) => {
      return forward(operation).map((response) => {
        if (response.data) {
          fixStockLogicForFE(response.data);
        }
        return response;
      });
    });

    return new ApolloClient({
      // clientMetadataLink và dynamicAuthLink đều spread `...headers` nên compose được,
      // không đè nhau — thứ tự giữa hai cái không quan trọng, miễn đứng trước httpLink.
      link: ApolloLink.from([errorLink, dynamicAuthLink, clientMetadataLink, fixStockLink, httpLink]),
      cache,
      defaultOptions: DEFAULT_OPTIONS,
      connectToDevTools: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { apolloClient };
};
